<script>
    import drag from '../private/drag';
    import resize from '../private/resize';
    import $util from '../../prototype/util';
    import { scrollProps } from '../private/util';
    import ibTableColgroup from './ib-table-colgroup';

    const indexOf = Array.prototype.indexOf;
    const rootMargin = 240;
    const inertia = 2;


    /**
     * A responsive table with sorting and filtering.
     */
    export default {
        name: 'ib-table-basic',

        mixins: [
            drag,
            resize
        ],

        components: {
            ibTableColgroup
        },

        props: {
            /**
             * An array of objects containing data used to render each row. Presentation is
             * dependent on the "body" scoped slot.
             */
            data: {
                type: Array,
                default: () => []
            },
            /**
             * Member of data object to use as emitted row ID, or function to retrieve such.
             * If you provide this as a function, **DO NOT** inline it into your template.
             */
            emit: [String,Number,Function],
            /**
             * DOM `id` attribute.
             */
            id: String,
            /**
             * Force this table's height.
             */
            height: String,
            /**
             * Force this table's maximum height.
             */
            maxHeight: String,
            /**
             * Row density. Supported are `compact`, `comfortable`, and `huge`.
             */
            spacing: String,
            /**
             * Padding at left and right ends of table. Supported are `0`, `sm`, `lg`, and `xl`.
             */
            inset: String,
            /**
             * If true, allow text to wrap. By default, wrapping is disabled.
             */
            wrap: Boolean,
            /**
             * If true, use `table-layout: auto`. Table columns will be dynamically sized to content.
             * `ib-table-col` can be omitted if this is used.
             */
            auto: Boolean,
            /**
             * If true, the table is treated as an `ib-row`.
             */
            row: Boolean,
            /**
             * Filter rows so only those containing the given string are visible.
             */
            filter: String,
            /**
             * Appearance modifier; `rounded` is supported.
             */
            appearance: String,
            /**
             * Header modifier; `transparent` is supported.
             */
            header: String,
            /**
             * Body modifier; `transparent` is supported.
             */
            body: String,
            /**
             * Footer modifier; `transparent` is supported.
             */
            footer: String,
            /**
             * If true, use zebra striping on alternating rows.
             */
            rowstripe: Boolean,
            /**
             * If true, display a hairline border between rows.
             */
            rowborder: {
                type: Boolean,
                default: true
            },
            /**
             * If true, display a hairline border between columns.
             */
            colborder: Boolean,
            /**
             * If true, and an underlay slot is used, display a kebab handle button.
             */
            handle: {
                type: Boolean,
                default: true
            }
        },

        data() {
            return {
                col: [],
                colhover: -1,
                ready: false,
                headless: false,
                slotStart: false,
                slotEnd: false,
                stickyStart: 0,
                stickyEnd: 0,
                shadowStart: -1,
                shadowEnd: -1,
                sort: {},
                lastStart: 0,
                lastEnd: 0,
                lastTop: 0,
                lastBottom: 0,
                lastWidth: null,
                lastHeight: null,
                ulid: -1,   // uloff is nonreactive
                rseW: true,
                rseH: this.lshow
            }
        },

        computed: {
            _id() {
                return this.id || '_tb' + this._uid;
            },

            _class() {
                const slot = this.slotStart || this.slotEnd;
                return {
                    _row: this.row,
                    _tbcl: this.lclick || slot,
                    _tbrs: this.rowstripe,
                    _tbrb: this.rowborder,
                    _tbcb: this.colborder,
                    _tbwr: this.wrap,
                    _tbaut: this.auto,
                    _tbu: slot,
                    _ltr: this.header && this.dir > 0,
                    _rtl: this.dir < 0,
                    [this.spacing]: this.spacing,
                    ['_tbi'+this.inset]: this.inset,
                    ['_tbe'+this.appearance]: this.appearance,
                    ['_tbh'+this.header]: this.header,
                    ['_tbb'+this.body]: this.body,
                    ['_tbf'+this.footer]: this.footer
                }
            },

            _on() {
                return {
                    scroll: this.scroll,
                    ...(this.lclick || this.slotStart || this.slotEnd ? {
                        click: this.eclick,
                        mouseover: this.mouseover,
                        touchstart: this.touchstart
                    } : {})
                };
            },

            dir() {
                return this.$rtl ? -1 : 1;
            },

            lclick() {
                return !!this.$listeners.click;
            },

            lshow() {
                return !!this.$listeners.show || !!this.$listeners.hide;
            },

            getid() {
                const emit = this.emit;
                if (typeof(emit) === 'function') {
                    return emit;
                } else if (emit != null) {
                    return row => row[emit];
                } else {
                    return (row,index) => index;
                }
            },

            tbsStart() {
                return this.dir < 0 ? '_tbsr' : '_tbsl';
            },

            tbsEnd() {
                return this.dir < 0 ? '_tbsl' : '_tbsr';
            },

            scrollProps() {
                const sp = scrollProps();
                return {
                    base: this.dir < 0 ? sp.rtlBase : 0,
                    step: this.dir < 0 ? sp.rtlStep : 1
                };
            },

            indexed() {
                let data = this.data;
                let r = [];
                for (let i = 0; i < data.length; i++) {
                    r[i] = {
                        data: data[i],
                        index: i,
                        id: this.getid(data[i], i)
                    };
                }
                return r;
            },

            sorted() {
                let r = this.indexed;
                const sortCol = this.sort.col;
                const sortDir = this.sort.dir;
                if (sortCol) {
                    const sortfn = sortCol.sortfn;
                    const options = {
                        numeric: true,
                        sensitivity: 'base'
                    };
                    if (sortfn) {
                        r = r.slice().sort((a, b) => {
                            a = sortfn(a.data);
                            b = sortfn(b.data);
                            return sortDir * (
                                typeof a === 'string' ? a.localeCompare(b, void 0, options)
                              : typeof b === 'string' ? -b.localeCompare(a, void 0, options)
                              : isNaN(a) ? -1
                              : isNaN(b) ? 1
                              : a - b);
                        });
                    }
                }
                return r;
            },

            /**
             * Object with keys equal to table row IDs and values equal to whether that row
             * should be visible under the current filter, if any.
             */
            v() {
                let r = {};
                if (this.filter) {
                    const filter = RegExp(this.filter.replace(/[\\^$.|?*+()[{]/g, '\\$&'), 'i');
                    for (let i = 0; i < this.indexed.length; i++) {
                        const tr = this.indexed[i];
                        r[tr.id] = this.col.some(col => col.sort && filter.test(col.sort(tr.data)));
                    }
                } else {
                    for (let i = 0; i < this.indexed.length; i++) {
                        const tr = this.indexed[i];
                        r[tr.id] = true;
                    }
                }
                return r;
            }
        },

        watch: {
            sort(to) {
                /**
                 * Emits a reference to a column if we begin sorting by that column or change sort direction.
                 * This event is emitted when the table is mounted if a column is marked as sorted.
                 * @event sort
                 * @type {ib-table-col}
                 */
                this.$emit('sort', this.sort.col);
            },

            colhover(to, from) {
                const classList = this.$refs.table.classList;
                if (from >= 0) {
                    classList.remove('_sh' + (from + 1));
                }
                if (to >= 0) {
                    classList.add('_sh' + (to + 1));
                }
            },

            stickyStart(to) {
                if (to) {
                    this.scroll();
                }
            },

            stickyEnd(to) {
                if (to) {
                    this.scroll();
                }
            }
        },

        created() {
            if ($build.env != 'production') {
                const spacing = this.spacing;
                if (spacing && spacing != 'compact' && spacing != 'comfortable' && spacing != 'huge') {
                    throw new RangeError('Invalid spacing value');
                }
                const inset = this.inset;
                if (inset && inset != '0' && inset != 'sm' && inset != 'lg' && inset != 'xl') {
                    throw new RangeError('Invalid inset value');
                }
            }
            this.parseNodes();
        },

        mounted() {
            this.ready = true;
            if (this.slotEnd != (!!this.$slots.end || this.$scopedSlots.end)) {
                this.parseNodes();
                this.$nextTick(() => {
                    const colgroup = this.$refs.colgroup;
                    if (colgroup) {
                        colgroup.refresh();
                    }
                });
            } else {
                this.$refs.colgroup.refresh();
            }
        },

        updated() {
            this.scroll();
        },

        beforeDestroy() {
            this.ready = false;
        },

        render(h) {
            const headStart = this.slotStart
                ? []
                : [
                    h('ib-table-col', {
                        'class': this.tbsStart,
                        'attrs': { width: '0', hidden: true }}) ];

            const headEnd = this.slotEnd
                ? [
                    h('ib-table-col', {
                        'class': this.tbsEnd,
                        'attrs': { width: '24px', force: true }}),
                    h('ib-table-col', {
                        'class': this.tbsEnd + 'e',
                        'attrs': { width: '0' }}) ]
                : [
                    h('ib-table-col', {
                        'class': this.tbsEnd,
                        'attrs': { width: '0', hidden: true }}) ];

            const footEnd = this.slotEnd
                ? [
                    h('td', {
                        'class': this.tbsEnd,
                        'attrs': { 'aria-hidden': 'true' }}),
                    h('div', {
                        'class': this.tbsEnd,
                        'attrs': { 'aria-hidden': 'true' }}) ]
                : [
                    h('div', {
                        'class': this.tbsEnd,
                        'attrs': { 'aria-hidden': 'true' }}) ];

            return h('div', {
                'staticClass': '_tbw _con',
                'class': this._class,
                'style': { height: this.height, maxHeight: this.maxHeight },
                'on': this._on
            }, [
                ...this.renderBefore(h),
                h('table', {
                    'ref': 'table',
                    'staticClass': '_tb',
                    'attrs': { id: this._id }
                }, [
                    h('ib-table-colgroup', {
                        'ref': 'colgroup',
                        'attrs': { col: this.col, ul: this.slotStart || this.slotEnd }
                    }),
                    this.$slots.caption ? h('caption', this.$slots.caption) : void 0,
                    h('thead', {
                        'staticClass': '_tbh'
                    }, [
                        this.$slots.head ? h('tr', [
                            h('div', {
                                'class': this.tbsStart,
                                'attrs': { 'aria-hidden': 'true' }
                            }),
                            this.$slots.head,
                            h('div', {
                                'class': this.tbsEnd,
                                'attrs': { 'aria-hidden': 'true' }
                            })
                        ]) : void 0,
                        h('tr', {
                            'style': this.headless ? 'display:none' : null
                        }, [
                            ...headStart,
                            ...this.renderHeadBefore(h),
                            this.$slots.default,
                            ...this.renderHeadAfter(h),
                            ...headEnd
                        ])
                    ]),
                    ...[ this.renderBody(h) ],
                    this.$slots.foot ? h('tfoot', {
                        'staticClass': '_tbf'
                    }, [
                        h('tr', [
                            h('div', {
                                'class': this.tbsStart,
                                'attrs': { 'aria-hidden': 'true' }
                            }),
                            this.$slots.foot,
                            ...footEnd
                        ])
                    ]) : void 0
                ])
            ]);
        },

        methods: {
            /**
             * Show the end underlay for the row with the given id.
             */
            showEnd(id, inplace) {
                this.uip = inplace;
                this.ulDragEnd();
                this.ulSet(id);
            },

            /**
             * Hide the end underlay if the given id is currently open.
             */
            hideEnd(id) {
                if (this.ulid === id) {
                    this.uip = false;
                    this.ulDragEnd();
                    this.ulSet();
                }
            },

            /** @private */
            renderBefore(h) {
                return [];
            },

            /** @private */
            renderHeadBefore(h) {
                return [];
            },

            /** @private */
            renderHeadAfter(h) {
                return [];
            },

            /** @private */
            renderHandleButton(h) {
                return this.handle ? [
                    h('div', {
                        'staticClass': 'fs7 fg30 text-center',
                    }, [
                        h('svg', { attrs: { viewBox: '0 0 4 16' } },[ h('path', { attrs: { d: 'M4 2a2 2 0 10-2 2 2 2 0 002-2zm0 6a2 2 0 10-2 2 2 2 0 002-2zm0 6a2 2 0 10-2 2 2 2 0 002-2z' }}) ])
                    ])] : void 0;
            },

            /** @private */
            renderBodyStart(h) {
                return [
                    h('div', {
                        'class': this.tbsStart,
                        'attrs': { 'aria-hidden': 'true' }
                    })];
            },

            /** @private */
            renderBodyEnd(h, row) {
                let end;
                if (row && this.slotEnd) {
                    end = this.$slots.end || this.$scopedSlots.end(row.data);
                    if (end && !end.some(el => el.tag)) {
                        end = void 0;
                    }
                }
                const ulid = this.ulid;
                return end
                    ? [
                        h('td', {
                            'class': row.id === ulid ? this.tbsEnd + ' _tbe' : this.tbsEnd
                        }, this.renderHandleButton(h)),
                        h('td', {
                            'staticClass': this.tbsEnd + 'e'
                        }, [
                            h('div', {
                                'staticClass': '_tbec',
                                'style': row.id === ulid ? 'transform:translateX(' + this.uloff + 'px)' : null,
                                'on': {
                                    'click': this.ulClose
                                }
                            }, [
                                end
                            ])
                        ])]
                    : [
                        h('div', {
                            'class': this.tbsEnd,
                            'attrs': { 'aria-hidden': true }})
                        ];
            },

            /** @private */
            renderSlot(name) {
                return this.$scopedSlots[name] || (this.$slots[name] ? () => this.$slots[name] : (data => data.map(e => h('td', [e]))));
            },

            /** @private */
            renderBody(h) {
                const body = this.renderSlot('body');
                const slot = this.slotStart || this.slotEnd;
                const ulid = this.ulid;
                const utr = !!this.utr;
                const inplace = this.uip;
                const rh = this.rowhover;
                return h('tbody', {
                        'ref': 'body',
                        'staticClass': '_tbb'
                    },
                    this.sorted.length
                        ? this.sorted.map((row,index) => h('tr', {
                                'class': {
                                    _trh: index === rh,
                                    _tro: row.id === ulid,
                                    _tui: row.id === ulid && inplace,
                                    _trs: row.id === ulid && utr
                                },
                                'style': this.v[row.id] ? row.id === ulid && !inplace ? 'transform:translateX(' + -this.uloff + 'px)' : null : 'display:none'
                            }, [
                                ...this.renderBodyStart(h, row),
                                body(row.data),
                                ...this.renderBodyEnd(h, row)
                            ]))
                        : this.renderEmpty(h));
            },

            /** @private */
            renderEmpty(h) {
                return [
                    h('tr', [
                        h('td', {
                            'attrs': { colspan: this.col.length }
                        }, [
                            ...this.renderBodyStart(h),
                            this.$slots.empty,
                            ...this.renderBodyEnd(h)
                        ])
                    ])
                ];
            },

            /** @private */
            sync() {
                if (this.ready) {
                    this.parseNodes();
                }
            },

            /** @private */
            parseNodes() {
                this.slotStart = !!this.$slots.start || !!this.$scopedSlots.start;
                this.slotEnd = !!this.$slots.end || !!this.$scopedSlots.end;
                const col = (this.$slots.default || []).filter(vnode =>
                    vnode.componentOptions &&
                    vnode.componentOptions.Ctor.options.name === 'ib-table-col');
                this.col = [
                    ...this.before(),
                    ...col.map(col => col.componentOptions.propsData),
                    ...this.after()
                ];
                if (this.sort.col && !col.includes(this.sort.col.$vnode)) {
                    this.sort = {};
                }
                this.headless = !col.some(vnode => vnode.componentOptions.children);
            },

            /** @private */
            before() {
                return [{ width: 0 }];
            },

            /** @private */
            after() {
                return (this.slotEnd ? [{ width: '24px', sticky: 'end', align: 'end', force: true }] : []).concat([{ width: 0 }]);
            },

            /** @private */
            resize() {
                const width = this.$el.offsetWidth;
                const height = this.$el.offsetHeight;
                if (this.lastWidth !== width) {
                    this.lastWidth = width;
                    this.$refs.colgroup.dropUpdate(this.$el.offsetWidth);
                    this.scroll();
                } else if (this.lshow && this.lastHeight !== height) {
                    this.lastHeight = height;
                    this.visibility();
                }
            },

            /** @private */
            scroll(e) {
                if (!this.slotStart && !this.slotEnd) {
                    const el = this.$el;
                    const start = Math.min(16, Math.max(0, this.scrollProps.base * (el.scrollWidth - el.offsetWidth) + this.scrollProps.step * el.scrollLeft) / 16 | 0);
                    if (this.shadowStart != start || e === undefined) {
                        const table = this.$refs.table;
                        table.classList.remove(this.tbsStart + this.shadowStart);
                        if (start) {
                            table.classList.add(this.tbsStart + start);
                        }
                        this.shadowStart = start;
                    }
                    const end = Math.min(16, Math.max(0, (1-this.scrollProps.base) * (el.scrollWidth - el.offsetWidth) - this.scrollProps.step * el.scrollLeft) / 16 | 0);
                    if (this.shadowEnd != end || e === undefined) {
                        const table = this.$refs.table;
                        table.classList.remove(this.tbsEnd + this.shadowEnd);
                        if (end) {
                            table.classList.add(this.tbsEnd + end);
                        }
                        this.shadowEnd = end;
                    }
                }
                if (this.lshow) {
                    this.visibility();
                }
            },

            /** @private */
            visibility() {
                const top = this.$el.scrollTop;
                const bottom = top + this.$el.clientHeight;
                const ltop = this.lastTop;
                const lbottom = this.lastBottom;

                if (ltop !== top || lbottom !== bottom) {
                    const children = this.$refs.body.children;
                    let si = this.lastStart;
                    let ei = this.lastEnd;
                    let hide = [];
                    let show = [];

                    if (ltop < top) {
                        if (si < 0) {
                            si = 0;
                        }
                        while (si < children.length && children[si].offsetTop + children[si].offsetHeight <= top - rootMargin) {
                            hide.push(this.sorted[si++].id);
                        }
                    } else if (ltop > top) {
                        if (si >= children.length) {
                            si = children.length - 1;
                        }
                        while (si >= 0 && children[si].offsetTop > top - rootMargin) {
                            show.push(this.sorted[si--].id);
                        }
                    }
                    if (lbottom < bottom) {
                        if (ei < 0) {
                            ei = 0;
                        }
                        while (ei < children.length && children[ei].offsetTop + children[ei].offsetHeight <= bottom + rootMargin) {
                            show.push(this.sorted[ei++].id);
                        }
                    } else if (lbottom > bottom) {
                        if (ei >= children.length) {
                            ei = children.length - 1;
                        }
                        while (ei >= 0 && children[ei].offsetTop > bottom + rootMargin) {
                            hide.push(this.sorted[ei--].id);
                        }
                    }

                    this.lastTop = top;
                    this.lastBottom = bottom;
                    this.lastStart = si;
                    this.lastEnd = ei;

                    if (show.length) {
                        /**
                         * Emits an array of row IDs that have just entered the viewport. This is
                         * useful for lazy loading data.
                         * @event show
                         * @type {Array}
                         */
                        this.$emit('show', show);
                    }
                    if (hide.length) {
                        /**
                         * Emits an array of row IDs that have just left the viewport.
                         * @event hide
                         * @type {Array}
                         */
                        this.$emit('hide', hide);
                    }
                }
            },

            /** @private */
            td(e) {
                return e.target.tagName == 'TD' ? e.target : $util.parent(e.target, el => el.tagName == 'TD');
            },

            /** @private */
            tr(td) {
                return td ? $util.parent(td, el => el.tagName == 'TR') : null;
            },

            /** @private */
            mouseover(e) {
                if (!this.disabled && !this.touched) {
                    const tr = this.hoverhook(e.target);
                    if (tr) {
                        tr.addEventListener('mouseleave', this.mouseleave);
                    }
                }
            },

            /** @private */
            mouseleave() {
                const rh = this.rowhover;
                if (rh != null) {
                    this.rowhover = null;
                    const tr = this.$refs.body.children[rh];
                    tr.classList.remove('_trh');
                    tr.removeEventListener('mouseleave', this.mouseleave);
                }
            },

            /** @private */
            touchstart(e) {
                if (!this.disabled && e.which == 0) {
                    if (this.hoverhook(e.target)) {
                        document.addEventListener('touchmove', this.touchend, false);
                        document.addEventListener('touchend', this.touchend, false);
                        this.ulDragStart(e);
                    }
                }
            },

            /** @private */
            touchend(e) {
                const rh = this.rowhover;
                if (rh != null && e.which == 0) {
                    const tr = this.$refs.body.children[rh];
                    tr.classList.remove('_trh');
                    document.removeEventListener('touchmove', this.touchend, false);
                    document.removeEventListener('touchend', this.touchend, false);
                    this.rowhover = null;
                    this.touched = true;
                    setTimeout(() => { this.touched = false; }, 72);
                }
            },

            /** @private */
            eclick(e) {
                if (
                    !this.udrag &&
                    !e.button &&
                    e.target.tagName != 'BUTTON' &&
                    !$util.parent(e.target, el => el.tagName == 'BUTTON')
                    ) {
                    const td = this.td(e);
                    const tr = this.tr(td);
                    if (tr) {
                        const tbody = this.$refs.body;
                        const ri = indexOf.call(tbody.children, tr);
                        const ci = indexOf.call(tr.children, td);
                        if (ri >= 0 && ci >= 0) {
                            // touch is handled in _dragup()
                            if (!this.touched && this.slotEnd) {
                                const row = this.sorted[ri];
                                if (this.uloff && row.id === this.ulid) {
                                    this.hideEnd(row.id);
                                } else {
                                    this.showEnd(row.id, true);
                                }
                            }
                            this.clickhook(ri, ci);
                            this.clickemit(ri, ci);
                        }
                    }
                }
            },

            /** @private */
            hoverhook(el) {
                const tb = this.$refs.body.children;
                const tr = this.tr(el);
                const rh = indexOf.call(tb, tr);
                if (rh >= 0) {
                    if (this.rowhover != null) {
                        tb[this.rowhover].classList.remove('_trh');
                    }
                    this.rowhover = rh;
                    tr.classList.add('_trh');
                    return tr;
                }
            },

            /** @private */
            clickhook() {
                // empty
            },

            /** @private */
            clickemit(ri, ci) {
                /**
                 * Emits the row data and the associated column component when a table cell is clicked.
                 * @event click
                 * @type {object}
                 */
                this.$emit('click', {
                    row: ri >= 0 ? this.sorted[ri].data : null,
                    col: ci >= 0 ? this.col[ci] : null,
                    index: ri
                });
            },

            /**
             * Set horizontal position of sliding row.
             * @private
             */
            ulPos(tr, td, x) {
                if (!this.uip) {
                    tr.style.transform = 'translateX(' + -x + 'px)';
                }
                td.style.transform = 'translateX(' + x + 'px)';
            },

            /**
             * Open or close sliding row.
             * @private
             */
            ulSet(id) {
                this.ulid = id;
                const index = id == null ? -1 : this.sorted.findIndex(row => row.id === id);
                if (index >= 0) {
                    const tr = this.$refs.body.children[index];
                    if (tr) {
                        const td = tr.lastChild.firstChild;
                        if (td) {
                            this.uloff = this.dir * (this.uip ? -1 : 1) * td.getBoundingClientRect().width | 0;
                            this.ulPos(tr, td, this.uloff);
                            this.$emit('open', id);
                            document.addEventListener('mousedown', this.ulCancel, true);
                            return;
                        }
                    }
                }
                document.removeEventListener('mousedown', this.ulCancel, true);
            },

            /**
             * Close sliding row on body click.
             * @private
             */
            ulCancel(e) {
                if (!e.button) {
                    const tr = this.tr(e.target);
                    if (!tr || !tr.classList.contains('_tro')) {
                        this.ulSet();
                    }
                }
            },

            /**
             * Close sliding row on button click.
             * @private
             */
            ulClose(e) {
                if (!e.button && e.target.tagName == 'BUTTON' || $util.parent(e.target, el => el.tagName == 'BUTTON')) {
                    this.ulSet();
                }
            },

            /**
             * Sliding row drag init. This is also called prior to click.
             * @private
             */
            ulDragStart(e) {
                if (!e.button) {
                    const tr = this.tr(e.target);
                    const index = indexOf.call(this.$refs.body.children, tr);
                    if (index >= 0) {
                        const td = tr.lastChild.firstChild;
                        if (td && !$util.isChildOrSelf(e.target, td)) {
                            if (this.utt) {
                                clearTimeout(this.utt);
                                this.utt = 0;
                            }
                            if (this.sorted[index].id === this.ulid) {
                                this.ulini =
                                this.ulmax = this.dir * this.uloff;
                            } else {
                                this.ulSet(this.sorted[index].id);
                                this.ulmax = this.dir * this.uloff;
                                this.ulini = 0;
                                this.uloff = 0;
                            }
                            this.utr = tr;
                            this.utd = td;
                            this._dragcapture(tr, e);
                        }
                    }
                }
            },

            /**
             * Sliding row drag finish or abort.
             * @private
             */
            ulDragEnd() {
                if (this.utr) {
                    this.utr.classList.remove('_trs');
                }
                this.utr =
                this.utd = null;
                this.utt = setTimeout(() => {
                    this.udrag = false;
                    this.utt = 0;
                }, 100);
            },

            /**
             * Sliding row drag move.
             * @private
             */
            _dragmove(x) {
                if (!this.udrag) {
                    this.udrag = true;
                    this.utr.classList.add('_trs');
                }
                const dir = this.dir;
                const ulmax = this.ulmax;
                x = -dir * x + this.ulini;
                if (x > ulmax) {
                    x = ulmax + (x - ulmax) / Math.log10(1 + x - ulmax);
                } else if (x < 0) {
                    x = 0;
                    //x /= Math.log10(1 - x);
                }
                // uloff is nonreactive
                this.ulPos(this.utr, this.utd, this.uloff = dir * x);
            },

            /**
             * Sliding row drag release.
             * @private
             */
            _dragup(x) {
                if (this.udrag) {
                    const dir = this.dir;
                    const duration = Date.now() - this.uts;
                    if (duration > 20) {
                        x += x * Math.abs(x * inertia / duration);
                    }
                    x = -dir * x + this.ulini;
                    const pos = x / this.ulmax;
                    if (pos > .5) {
                        this.ulPos(this.utr, this.utd, this.uloff = this.dir * this.ulmax);
                    } else {
                        this.ulSet();
                    }
                    this.ulDragEnd();
                } else if (this.uloff) {
                    this.hideEnd(this.ulid);
                } else {
                    this.showEnd(this.ulid);
                }
            }
        }
    }
</script>

<style lang="less">
    @import "../../less/globals.less";

    @tr-padx: 16px;
    @tr-padx-0: 0;
    @tr-padx-sm: 8px;
    @tr-padx-lg: 24px;
    @tr-padx-xl: 32px;
    @th-pad: 3px 8px;
    @td-pad: 8px;
    @th-pady-compact: 0;
    @td-pady-compact: 2px;
    @th-pady-comfortable: 8px;
    @td-pady-comfortable: 12px;
    @th-pady-huge: 12px;
    @td-pady-huge: 18px;
    @th-color: fade(@fg, 46%);

    @highlight1: mix(@bg-highlight, @bg, @hover-opacity);
    @highlight2: mix(@bg-highlight, mix(@fg-max, @bg, 2%), @hover-opacity);

    ._tbw {
        position: relative;
        color: @fg;
        white-space: nowrap;
        overflow: auto;
        .font-size(@fs6);
        .line-height(round(@fs6 * 1.125 + 2));
        -webkit-overflow-scrolling: touch;
        > .progress {
            top: calc(10rem / @font-base ~' + 14px');
        }
    }
    ._tbu {
        overflow-x: hidden;
        tbody tr {
            touch-action: pan-y;
            -webkit-tap-highlight-color: transparent;
        }
    }
    ._tb {
        --highlight1: @highlight1;
        --highlight2: @highlight2;

        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        table-layout: fixed;

        col.hide {
            visibility: collapse;
            width: 0 !important;
            border: 0;
        }
        thead {
            z-index: 3;
        }
        caption,
        th {
            .font-size(@fs8);
            line-height: 2;
            font-weight: 500;
            color: @th-color;
            padding: @th-pad;
            border-bottom: 1px solid @border-default2;

            > ._thc {
                display: inline-block;
                vertical-align: bottom;
                max-width: calc(~'100% - 1em');
                overflow: hidden;
                text-overflow: ellipsis;
            }
            > ._tha svg {
                width: 15px;
                height: 15px;
                opacity: .4;
                vertical-align: -6px;
            }
        }
        th {
            position: -webkit-sticky;
            position: sticky;
            top: 0;
            background: @bg;
            cursor: default;
            z-index: 2;
            text-align: start;
            .table-th();
            &.sort {
                cursor: pointer;
            }
            &.sorted {
                > ._tha svg {
                    opacity: 1;
                }
            }
        }
        td {
            position: relative;
            padding: @td-pad;
            vertical-align: baseline;
            text-overflow: ellipsis;
            overflow: hidden;
            background: @bg;
            z-index: 0;
        }

        /** Start/end padding */
        th:first-of-type,
        td:first-of-type {
            -webkit-padding-start: @tr-padx;
            padding-inline-start: @tr-padx;
        }
        th:last-of-type,
        td:last-of-type {
            -webkit-padding-end: @tr-padx;
            padding-inline-end: @tr-padx;
        }
    }

    /** End cell */
    ._tb {
        ._tbsl,
        ._tbsr {
            display: table-cell;
            position: -webkit-sticky;
            position: sticky;
            padding: 0 !important;
            overflow: visible;
            z-index: 4;
        }
    }
    ._tbsl::before,
    ._tbsr::before {
        opacity: 0;
    }
    ._tbsl::before,
    ._tbsr::before,
    ._tbsl._tbe::after,
    ._tbsr._tbe::after {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        width: 24px;
        pointer-events: none;
    }
    ._tbsl::before,
    ._tbsr._tbe::after {
        left: 100%;
        background:
            linear-gradient(90deg, @table-shadow 0, transparent 100%),
            linear-gradient(90deg, @table-shadow 0, transparent 50%),
            linear-gradient(90deg, @table-shadow 0, transparent 25%);
    }
    ._tbsr::before,
    ._tbsl._tbe::after {
        right: 100%;
        background:
            linear-gradient(270deg, @table-shadow 0, transparent 100%),
            linear-gradient(270deg, @table-shadow 0, transparent 50%),
            linear-gradient(270deg, @table-shadow 0, transparent 25%);
    }
    ._tbh ._tbsl::before {
        background:
            radial-gradient(at bottom left, @table-shadow 0, transparent 100%),
            radial-gradient(at bottom left, @table-shadow 0, transparent 50%),
            radial-gradient(at bottom left, @table-shadow 0, transparent 25%);
    }
    ._tbh ._tbsr::before {
        background:
            radial-gradient(at bottom right, @table-shadow 0, transparent 100%),
            radial-gradient(at bottom right, @table-shadow 0, transparent 50%),
            radial-gradient(at bottom right, @table-shadow 0, transparent 25%);
    }

    /** Sliding underlay */
    ._tb {
        ._tbsls,
        ._tbsrs,
        ._tbsle,
        ._tbsre {
            position: -webkit-sticky;
            position: sticky;
            padding: 0 !important;
            z-index: -1;
            overflow: visible;
        }
        ._tbsls,
        ._tbsle {
            left: 0;
        }
        ._tbsrs,
        ._tbsre {
            right: 0;
        }
    }
    ._tbsc,
    ._tbec {
        position: absolute;
        display: inline-flex;
        align-items: stretch;
        top: 0;
        bottom: 0;
        visibility: hidden;
        ._btn {
            max-width: none;
            height: 100%;
            margin: 0;
        }
    }
    /** Stretch button color for elastic drag; hacky but seems reliable */
    ._tro:not(._tui) {
        ._tbsc,
        ._tbec {
            > ._btn:first-child,
            > :first-child > ._btn:first-child {
                -webkit-margin-start: -512px;
                -webkit-padding-start: 512px + @btn-padding;
                margin-inline-start: -512px;
                padding-inline-start: 512px + @btn-padding;
            }
        }
    }
    ._tbsc,
    ._tbw._rtl ._tbec {
        left: 0;
        right: auto;
    }
    ._tbec,
    ._tbw._rtl ._tbsc {
        left: auto;
        right: 0;
    }
    ._trs {
        will-change: transform;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
    }
    ._tb {
        tr,
        ._tbsc,
        ._tbec {
            transition: visibility 0s linear .3s, transform .3s;
        }
        ._trs,
        ._trs ._tbsc,
        ._trs ._tbec {
            transition: none;
        }
    }
    ._tro {
        ._tbsc,
        ._tbec {
            visibility: visible;
            transition: transform .3s;
        }
    }

    /** In-place start/end slot; specific to mouse click event */
    ._tui {
        ._tbsc,
        ._tbec {
            left: auto !important;
            right: auto !important;
        }
        ._tbsls,
        ._tbsrs,
        ._tbsle,
        ._tbsre {
            z-index: 3;
        }
    }

    /** Footer */
    ._tbf {
        td {
            border-top: 1px solid @border-default;
        }
    }

    /** Sticky column shadow opacity */
    .gen-shadow(16);
    .gen-shadow(@n, @i: 1) when (@i <= @n) {
        ._tbsl@{i} ._tbsl::before,
        ._tbsr@{i} ._tbsr::before {
            opacity: @i / 16;
            transition: opacity unit(@i / 32, s);
        }
        .gen-shadow(@n, (@i + 1));
    }

    /** Row stripe */
    ._tbrs {
        ._tbb > tr:nth-child(2n) {
            > td {
                background-color: mix(@fg-max, @bg, 2%);
            }
        }
    }

    /** Row border */
    ._tbrb {
        td {
            border-bottom: 1px solid @border-default;
        }
        ._tbb > tr:last-child > td {
            border-bottom: none;
            background-image: none;
        }
    }

    /** Column border */
    ._tbcb {
        th, td {
            border-right: 1px solid @border-default;
        }
    }

    /** Subpixel row and column borders */
    @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 2dppx) {
        ._tbrb {
            td {
                border-bottom: .5px solid @border-default2;
            }
        }
        ._tbcb {
            th, td {
                border-right: .5px solid @border-default2;
            }
        }
        ._spb ._tbrb {
            td {
                border-bottom: none;
                background-image:
                    linear-gradient(0deg, @border-default2 0, @border-default2 .5px, transparent 0);
            }
        }
        ._spb ._tbcb {
            td {
                border-right: none;
                background-image:
                    linear-gradient(270deg, @border-default2 0, @border-default2 .5px, transparent 0);
            }
        }
        ._spb ._tbrb._tbcb {
            td {
                background-image:
                    linear-gradient(0deg, @border-default2 0, @border-default2 .5px, transparent 0),
                    linear-gradient(270deg, @border-default2 0, @border-default2 .5px, transparent 0);
            }
        }
    }
    @media (-webkit-min-device-pixel-ratio: 3) and (-webkit-max-device-pixel-ratio: 3.99), (min-resolution: 3dppx) and (max-resolution: 3.99dppx) {
        ._tbrb {
            td {
                border-bottom: .333333px solid @border-default3;
            }
        }
        ._tbcb {
            th, td {
                border-right: .333333px solid @border-default3;
            }
        }
        ._spb ._tbrb {
            td {
                border-bottom: none;
                background-image:
                    linear-gradient(0deg, @border-default3 0, @border-default3 .333333px, transparent 0);
            }
        }
        ._spb ._tbcb {
            td {
                border-right: none;
                background-image:
                    linear-gradient(270deg, @border-default3 0, @border-default3 .333333px, transparent 0);
            }
        }
        ._spb ._tbrb._tbcb {
            td {
                background-image:
                    linear-gradient(0deg, @border-default3 0, @border-default3 .333333px, transparent 0),
                    linear-gradient(270deg, @border-default3 0, @border-default3 .333333px, transparent 0);
            }
        }
    }

    /** Row click cursor */
    ._tbcl {
        tbody tr {
            cursor: pointer;
            -webkit-tap-highlight-color: transparent;
        }
        tbody tr._da {
            cursor: default;
        }
    }

    /** Row hover */
    ._trh > td {
        background-color: @highlight1;
    }
    ._tbrs {
        ._tbb > ._trh:nth-child(2n) > td {
            background-color: @highlight2;
        }
    }

    /** Text wrap */
    ._tbwr {
        white-space: normal;
    }

    /** Auto column widths */
    ._tbaut > ._tb {
        table-layout: auto;
    }

    /** Spacing modifiers */
    ._tbw {
        &.wrap {
            white-space: normal;
        }
        &.compact {
            th {
                padding-top: @th-pady-compact;
                padding-bottom: @th-pady-compact;
            }
            td {
                padding-top: @td-pady-compact;
                padding-bottom: @td-pady-compact;
            }
        }
        &.comfortable {
            th {
                padding-top: @th-pady-comfortable;
                padding-bottom: @th-pady-comfortable;
            }
            td {
                padding-top: @td-pady-comfortable;
                padding-bottom: @td-pady-comfortable;
            }
        }
        &.huge {
            th {
                padding-top: @th-pady-huge;
                padding-bottom: @th-pady-huge;
            }
            td {
                padding-top: @td-pady-huge;
                padding-bottom: @td-pady-huge;
            }
        }
        &._tbi0 {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-0;
                padding-inline-start: @tr-padx-0;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-0;
                padding-inline-end: @tr-padx-0;
            }
        }
        &._tbism {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-sm;
                padding-inline-start: @tr-padx-sm;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-sm;
                padding-inline-end: @tr-padx-sm;
            }
        }
        &._tbilg {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-lg;
                padding-inline-start: @tr-padx-lg;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-lg;
                padding-inline-end: @tr-padx-lg;
            }
        }
        &._tbixl {
            th:first-of-type,
            td:first-of-type {
                -webkit-padding-start: @tr-padx-xl;
                padding-inline-start: @tr-padx-xl;
            }
            th:last-of-type,
            td:last-of-type {
                -webkit-padding-end: @tr-padx-xl;
                padding-inline-end: @tr-padx-xl;
            }
        }
    }

    /** Appearance modifiers */
    ._tberounded:not(._tbhtransparent) {
        border-radius: 8px;
    }
    ._tbhtransparent {
        th {
            position: static;
            background: none;
            .table-th-transparent();
        }
    }
    ._tbbtransparent {
        td {
            background: none;
        }
    }
    ._tberounded._tbhtransparent._ltr {
        caption,
        th {
            border-bottom: none;
        }
        tbody:first-of-type > tr:first-of-type {
            > td:first-of-type {
                border-top-left-radius: 8px;
            }
            > td:last-of-type {
                border-top-right-radius: 8px;
            }
        }
        tbody:last-of-type > tr:last-of-type {
            > td:first-of-type {
                border-bottom-left-radius: 8px;
            }
            > td:last-of-type {
                border-bottom-right-radius: 8px;
            }
        }
    }
    ._tberounded._tbhtransparent._rtl {
        tbody:first-of-type > tr:first-of-type {
            > td:first-of-type {
                border-top-right-radius: 8px;
            }
            > td:last-of-type {
                border-top-left-radius: 8px;
            }
        }
        tbody:last-of-type > tr:last-of-type {
            > td:first-of-type {
                border-bottom-right-radius: 8px;
            }
            > td:last-of-type {
                border-bottom-left-radius: 8px;
            }
        }
    }

    /** Form control style modifiers */
    ._tb {
        ._check {
            &:not(._edefault) > ._cbc {
                .checkbox-frameless();
            }
            &:not(._edefault) > input:checked + ._cbc {
                .checkbox-frameless-checked();
            }
        }
        ._radio {
            &:not(._edefault) > ._cbc {
                .radio-frameless();
            }
            &:not(._edefault) > input:checked + ._cbc {
                .radio-frameless-checked();
            }
        }
        ._toggle {
            margin: -4px 0;
        }
        ._field,
        ._pick {
            &:not(._ebox):not(._eunderline) {
                .field-frameless();
                margin: -4px 0;
            }
        }
        ._fldd {
            &:not(._ebox):not(._eunderline) {
                .dropdown-frameless();
                margin: -4px 0;
            }
        }
    }
</style>

<docs>
    Examples:
    ```jsx
        new Vue({
            data() { return {
                sample1: [
                    { currency: 'USD', amount: 20, note: 'Twenty US dollars' },
                    { currency: 'GBP', amount: 35, note: 'Thirty-five British pounts' },
                    { currency: 'NZD', amount: 12, note: 'Twelve New Zealand dollars' },
                    { currency: 'EUR', amount: 600, note: 'Six hundred euros' },
                    { currency: 'AZN', amount: 40000, note: 'Forty thousand Azerbaijan Manat' },
                    { currency: 'JPY', amount: 1000, note: 'One thousand Japanese Yen' },
                    { currency: 'AED', amount: 7500, note: 'Seven thousand five hundred UAE Dirham' },
                    { currency: 'CNY', amount: 12000, note: 'Twelve thousand Yuan Renminbi' },
                    { currency: 'INR', amount: 400, note: 'Four hundred Indian Rupees' }
                ]
            }},
            template:
                `<ib-context>
                    <p class="after-md">Basic Example:</p>
                    <ib-table-basic :data="sample1">
                        <ib-table-col sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col>Note</ib-table-col>
                        <ib-table-col sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-basic>

                    <p class="before-xl after-md">With column widths:</p>
                    <ib-table-basic :data="sample1">
                        <ib-table-col width="8ch" sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col width="24ch">Note</ib-table-col>
                        <ib-table-col width="12ch" sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-basic>

                    <p class="before-xl after-md">With droppable middle column:</p>
                    <ib-table-basic :data="sample1">
                        <ib-table-col width="8ch" sort="currency" sorted>Currency</ib-table-col>
                        <ib-table-col width="24ch" drop>Note</ib-table-col>
                        <ib-table-col width="12ch" sort="amount" numeric>Amount</ib-table-col>
                        <template slot="body" slot-scope="pos">
                            <td>{{ pos.currency }}</td>
                            <td>{{ pos.note }}</td>
                            <td>{{ pos.amount }}</td>
                        </template>
                    </ib-table-basic>
                </ib-context>`
        });
    ```
</docs>
